import React, { PropsWithChildren } from 'react';
import { Box, Button, Chip, Drawer, IconButton, SxProps, Typography, colors } from '@mui/material';
import { Close } from '@mui/icons-material';

import { InfoTooltip } from '../../../../components/core';
import SkipifyButton from '../../../../components/core/Button/Button';

/** Extra space below a section's "Select all" row before individual options. */
export const filterSelectAllRowSx: SxProps = {
  mb: 1,
};

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onReset: () => void;
  onSubmit: () => void;
  filtersCount: number;
  isSubmitting?: boolean;
}

export const BaseFilterSection = ({
  title,
  required,
  tooltip,
  children,
  sx,
}: {
  title: string;
  tooltip?: string;
  children: React.ReactNode;
  sx?: SxProps;
  required?: boolean;
}) => (
  <Box component="section" sx={{ mt: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
      <Typography component="h3" fontWeight="semibold">
        {title}
        {required && (
          <Typography sx={{ color: 'error.main', display: 'inline', ml: 0.5 }}>*</Typography>
        )}
      </Typography>
      {(tooltip?.length ?? 0) > 0 && (
        <InfoTooltip
          title={tooltip}
          placement="right"
          iconTone="muted"
          ariaLabel={`${title} help`}
        />
      )}
    </Box>
    <Box sx={sx}>{children}</Box>
  </Box>
);

export const BaseFiltersPanel = ({
  isOpen,
  onCancel,
  onReset,
  onSubmit,
  filtersCount,
  isSubmitting,
  children,
}: PropsWithChildren<Props>) => {
  return (
    <Drawer anchor="right" open={isOpen} onClose={onCancel}>
      <Box
        sx={{
          width: { xs: '100vw', sm: '440px' },
          height: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
        }}
        component="form"
        id="filters-form"
        onSubmit={onSubmit}
      >
        <Box
          sx={{
            py: 4,
            px: { xs: 4, sm: 6 },
            overflow: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography component="h2" fontWeight="semibold" variant="xl">
              Filters
            </Typography>
            <IconButton onClick={onCancel}>
              <Close sx={{ color: 'common.black', fontSize: 24 }} />
            </IconButton>
          </Box>
          <Chip
            label={`${filtersCount ?? '0'} filters`}
            sx={{
              mt: 2,
              mb: -1,
              fontSize: 14,
            }}
          />
          {children}
        </Box>
        <Box
          sx={{
            borderTop: `1px solid ${colors.grey[300]}`,
            display: 'flex',
            justifyContent: 'space-between',
            p: { xs: 1, sm: 2 },
            gap: 2,
            '& button': {
              height: '50px',
            },
          }}
        >
          <Button
            variant="text"
            sx={{
              textTransform: 'capitalize',
              color: 'link.main',
              fontWeight: 600,

              fontSize: 14,
              letterSpacing: 'initial',
              borderRadius: '50px',
              px: { xs: 0.5, sm: 2 },
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={onReset}
          >
            Clear all filters
          </Button>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              '& button': {
                px: { xs: 2, sm: 4 },
              },
            }}
          >
            <SkipifyButton text="Cancel" theme="secondary" onClick={onCancel} />
            <SkipifyButton text="Apply" type="submit" form="filters-form" loading={isSubmitting} />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};
